class KeSearchSuggest {
	constructor() {
		this.formSelector = 'form[data-suggest]'
		this.inputSelector = 'input[data-search-suggest]'
		this.itemSelector = '[data-suggestion-link]'
		this.init()
	}

	init() {
		document.querySelectorAll(this.formSelector).forEach(form => this.initializeForm(form))
	}

	initializeForm(form) {
		if (form.dataset.searchSuggestInitialized === '1') {
			return
		}

		const input = form.querySelector(this.inputSelector)
		const suggestions = form.querySelector('.search__suggestions')
		const suggestionList = suggestions?.querySelector('ul')

		if (!input || !suggestions || !suggestionList) {
			return
		}

		form.dataset.searchSuggestInitialized = '1'

		let debounceTimer = null
		let abortController = null
		let activeIndex = -1

		const hideSuggestions = () => {
			activeIndex = -1
			suggestionList.innerHTML = ''
			suggestions.style.display = 'none'
			form.classList.remove('autocomplete-active')
		}

		const escapeHtml = value =>
			String(value ?? '')
				.replace(/&/g, '&amp;')
				.replace(/</g, '&lt;')
				.replace(/>/g, '&gt;')
				.replace(/"/g, '&quot;')
				.replace(/'/g, '&#39;')

		const highlightQuery = (value, query) => {
			const escapedValue = escapeHtml(value)
			const trimmedQuery = String(query || '').trim()

			if (trimmedQuery === '') {
				return escapedValue
			}

			const escapedQuery = trimmedQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
			return escapedValue.replace(new RegExp(`(${escapedQuery})`, 'ig'), '<strong>$1</strong>')
		}

		const normalizeResponse = response => {
			if (!response) {
				return []
			}

			if (Array.isArray(response.documents)) {
				return response.documents
					.filter(item => item && item.title)
					.map(item => ({
						title: item.title,
						link: item.link || '',
					}))
			}

			if (response.suggestions && typeof response.suggestions === 'object') {
				return Object.keys(response.suggestions)
					.filter(title => title && title.trim() !== '')
					.map(title => ({
						title,
						link: '',
					}))
			}

			return []
		}

		const renderSuggestions = items => {
			suggestionList.innerHTML = ''
			activeIndex = -1
			const currentQuery = input.value.trim()

			if (!items.length) {
				hideSuggestions()
				return
			}

			items.forEach((item, index) => {
				const entry = document.createElement('li')
				entry.className = 'search__suggestion-item'

				const link = document.createElement('a')
				link.className = 'search__suggestion-link'
				link.href = item.link || '#'
				link.dataset.suggestionLink = '1'
				link.dataset.index = String(index)
				link.innerHTML = highlightQuery(item.title || '', currentQuery)

				if (!item.link) {
					link.setAttribute('role', 'button')
				}

				entry.appendChild(link)
				suggestionList.appendChild(entry)
			})

			suggestions.style.display = 'block'
			form.classList.add('autocomplete-active')
		}

		const fetchSuggestions = query => {
			if (abortController) {
				abortController.abort()
			}

			abortController = new AbortController()
			const url = new URL(form.dataset.suggest, window.location.origin)
			url.searchParams.set('queryString', query)

			fetch(url.toString(), {
				signal: abortController.signal,
				headers: {
					Accept: 'application/json',
					'X-Requested-With': 'XMLHttpRequest',
				},
			})
				.then(response => {
					if (!response.ok) {
						throw new Error('Suggestion request failed')
					}

					return response.json()
				})
				.then(response => {
					renderSuggestions(normalizeResponse(response))
				})
				.catch(error => {
					if (error.name !== 'AbortError') {
						hideSuggestions()
					}
				})
		}

		form.addEventListener('submit', event => {
			if (input.value.trim() === '') {
				event.preventDefault()
				input.focus()
			}
		})

		input.addEventListener('input', () => {
			const query = input.value.trim()
			window.clearTimeout(debounceTimer)

			if (query.length < 2) {
				hideSuggestions()
				return
			}

			debounceTimer = window.setTimeout(() => {
				fetchSuggestions(query)
			}, 150)
		})

		input.addEventListener('keydown', event => {
			const items = suggestionList.querySelectorAll(this.itemSelector)
			if (!items.length) {
				return
			}

			if (event.key === 'ArrowDown') {
				event.preventDefault()
				activeIndex = (activeIndex + 1) % items.length
			} else if (event.key === 'ArrowUp') {
				event.preventDefault()
				activeIndex = activeIndex <= 0 ? items.length - 1 : activeIndex - 1
			} else if (event.key === 'Enter' && activeIndex >= 0) {
				event.preventDefault()
				items[activeIndex].click()
				return
			} else if (event.key === 'Escape') {
				hideSuggestions()
				return
			} else {
				return
			}

			items.forEach(item => item.classList.remove('is-active'))
			items[activeIndex].classList.add('is-active')
		})

		suggestionList.addEventListener('mousedown', event => {
			const target = event.target.closest(this.itemSelector)
			if (!target) {
				return
			}

			if (!target.getAttribute('href') || target.getAttribute('href') === '#') {
				event.preventDefault()
				form.requestSubmit()
			}
		})

		document.addEventListener('click', event => {
			if (!form.contains(event.target)) {
				hideSuggestions()
			}
		})
	}
}

if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => new KeSearchSuggest())
} else {
	new KeSearchSuggest()
}

window.KeSearchSuggest = KeSearchSuggest
